import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { findDocumentBySlug } from '../utils/slugUtils';
import { fetchBlogPosts } from '../services/dataService';
import './BlogDetail.css';

const BlogDetail = () => {
  const { blogSlug } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    // Load blog post data from Firebase using slug
    const fetchBlogPost = async () => {
      setLoading(true);
      try {
        const foundPost = await findDocumentBySlug(blogSlug, 'blogs');
        setBlogPost(foundPost);
        
        // Get related posts
        const allPosts = await fetchBlogPosts();
        const related = allPosts
          .filter(p => p.id !== foundPost?.id && p.category === foundPost?.category)
          .slice(0, 3);
        setRelatedPosts(related);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [blogSlug]);

  if (loading) {
    return (
      <div className="blog-detail-loading">
        <div className="container">
          <div className="loading-content">
            <div className="loading"></div>
            <p>Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div className="blog-not-found">
        <div className="container">
          <div className="not-found-content">
            <h1>Article Not Found</h1>
            <p>The article you're looking for doesn't exist.</p>
            <Link to="/blog" className="btn btn-primary">
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="blog-detail">
      {/* Breadcrumb */}
      <section className="breadcrumb-section">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/blog" className="breadcrumb-link">Blog</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{blogPost.title}</span>
          </nav>
        </div>
      </section>

      {/* Article Header */}
      <section className="article-header">
        <div className="container">
          <div className="article-meta">
            <span className="article-category">{blogPost.category}</span>
            <span className="article-date">{formatDate(blogPost.publishedAt)}</span>
            <span className="article-read-time">{blogPost.readTime}</span>
          </div>
          <h1 className="article-title">{blogPost.title}</h1>
          <div className="article-stats">
            <span className="article-views">{blogPost.views} views</span>
            <span className="article-likes">{blogPost.likes} likes</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="article-image">
        <div className="container">
          <div className="featured-image">
            <img src={blogPost.image} alt={blogPost.title} />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="article-content">
        <div className="container">
          <div className="content-grid">
            {/* Main Content */}
            <div className="main-content">
              <div className="article-body">
                <div 
                  className="article-text"
                  dangerouslySetInnerHTML={{ __html: blogPost.content }}
                />
              </div>

              {/* Tags */}
              <div className="article-tags">
                <h3>Tags</h3>
                <div className="tags-list">
                  {blogPost.tags.map(tag => (
                    <span key={tag} className="tag">#{tag}</span>
                  ))}
                </div>
              </div>

              {/* Author Info */}
              <div className="author-info">
                <div className="author-avatar">
                  <img src="/logo.png" alt={blogPost.author} />
                </div>
                <div className="author-details">
                  <h3>Written by {blogPost.author}</h3>
                  <p>Our team of gardening experts shares their knowledge and experience to help you grow beautiful, healthy plants.</p>
                </div>
              </div>

              {/* Social Sharing */}
              <div className="social-sharing">
                <h3>Share this article</h3>
                <div className="share-buttons">
                  <button className="share-btn facebook">Facebook</button>
                  <button className="share-btn twitter">Twitter</button>
                  <button className="share-btn linkedin">LinkedIn</button>
                  <button className="share-btn whatsapp">WhatsApp</button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="sidebar">
              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>Table of Contents</h3>
                <ul>
                  <li><a href="#introduction">Introduction</a></li>
                  <li><a href="#tips">Essential Tips</a></li>
                  <li><a href="#conclusion">Conclusion</a></li>
                </ul>
              </div>

              {/* Related Articles */}
              <div className="related-articles">
                <h3>Related Articles</h3>
                <div className="related-list">
                  {relatedPosts.map(post => (
                    <div key={post.id} className="related-item">
                      <div className="related-image">
                        <img src={post.image} alt={post.title} />
                      </div>
                      <div className="related-content">
                        <h4><Link to={`/blog/${post.slug || post.id}`}>{post.title}</Link></h4>
                        <p>{post.excerpt}</p>
                        <span className="related-date">{formatDate(post.publishedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="blog-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Gardening?</h2>
            <p>Explore our range of quality fertilizers and gardening supplies.</p>
            <div className="cta-actions">
              <Link to="/products" className="btn btn-primary">
                Browse Products
              </Link>
              <Link to="/contact" className="btn btn-primary">
                Get Expert Advice
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;